/**
 * Mock data for the VAS Commitment Command Center.
 * Synthetic data only — no real companies or financial institutions referenced.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type VASCommitmentType = 'VIK' | 'Incentive' | 'Rebate' | 'Credit' | 'Partner Obligation';
export type VASRiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';
export type VASStatus = 'On Track' | 'At Risk' | 'Expiring' | 'Approval Needed' | 'Escalated';

export type VASFilterCategory =
  | 'all'
  | 'expiring_7_days'
  | 'expiring_30_days'
  | 'high_risk'
  | 'approval_needed'
  | 'no_rollover'
  | 'missing_owner'
  | 'contract_mismatch';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface VASCommitment {
  caseId: string;
  partner: string;
  commitmentType: VASCommitmentType;
  allocation: number;
  drawn: number;
  remaining: number;
  daysLeft: number;
  risk: VASRiskLevel;
  status: VASStatus;
  tags: string[];
}

export interface VASMetric {
  label: string;
  value: string;
  trend?: string;
}

export interface VASFilterOption {
  key: VASFilterCategory;
  label: string;
}

// ─── Filter Logic ────────────────────────────────────────────────────────────

export function matchesFilter(commitment: VASCommitment, filter: VASFilterCategory): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'expiring_7_days':
      return commitment.daysLeft <= 7;
    case 'expiring_30_days':
      return commitment.daysLeft <= 30;
    case 'high_risk':
      return commitment.risk === 'High' || commitment.risk === 'Critical';
    case 'approval_needed':
      return commitment.status === 'Approval Needed';
    case 'no_rollover':
      return commitment.tags.includes('no_rollover');
    case 'missing_owner':
      return commitment.tags.includes('missing_owner');
    case 'contract_mismatch':
      return commitment.tags.includes('contract_mismatch');
  }
}

// ─── Mock Commitment Data ────────────────────────────────────────────────────

export const mockVASCommitments: VASCommitment[] = [
  {
    caseId: 'VIK-2024-033',
    partner: 'AeroConnect Airlines',
    commitmentType: 'Credit',
    allocation: 4_000_000,
    drawn: 2_400_000,
    remaining: 1_600_000,
    daysLeft: 7,
    risk: 'Critical',
    status: 'Expiring',
    tags: ['no_rollover'],
  },
  {
    caseId: 'VIK-2024-041',
    partner: 'MetroBank Merchant Services',
    commitmentType: 'Credit',
    allocation: 2_500_000,
    drawn: 1_100_000,
    remaining: 1_400_000,
    daysLeft: 14,
    risk: 'High',
    status: 'At Risk',
    tags: ['missing_owner'],
  },
  {
    caseId: 'VIK-2024-052',
    partner: 'ShopSphere Marketplace',
    commitmentType: 'Incentive',
    allocation: 1_800_000,
    drawn: 1_200_000,
    remaining: 600_000,
    daysLeft: 21,
    risk: 'High',
    status: 'Approval Needed',
    tags: ['contract_mismatch'],
  },
  {
    caseId: 'VIK-2024-058',
    partner: 'NorthStar Retail Group',
    commitmentType: 'Credit',
    allocation: 3_200_000,
    drawn: 2_900_000,
    remaining: 300_000,
    daysLeft: 5,
    risk: 'Medium',
    status: 'On Track',
    tags: ['no_rollover'],
  },
  {
    caseId: 'VIK-2024-061',
    partner: 'Horizon Travel Network',
    commitmentType: 'Incentive',
    allocation: 5_000_000,
    drawn: 3_800_000,
    remaining: 1_200_000,
    daysLeft: 29,
    risk: 'Critical',
    status: 'At Risk',
    tags: ['missing_owner', 'no_rollover'],
  },
];

// ─── Mock Metrics ────────────────────────────────────────────────────────────

export const mockVASMetrics: VASMetric[] = [
  { label: 'Active VIK/incentive commitments', value: '$186M' },
  { label: 'At-risk next 30 days', value: '$14.8M' },
  { label: 'Expiring in 7 days', value: '$4.0M' },
  { label: 'Forecasted forfeiture risk', value: '$1.2M' },
  { label: 'Open exceptions', value: '23' },
  { label: 'Value preserved this quarter', value: '$9.6M' },
];

// ─── Filter Categories ───────────────────────────────────────────────────────

export const vasFilterCategories: VASFilterOption[] = [
  { key: 'all', label: 'All commitments' },
  { key: 'expiring_7_days', label: 'Expiring in 7 days' },
  { key: 'expiring_30_days', label: 'Expiring in 30 days' },
  { key: 'high_risk', label: 'High risk' },
  { key: 'approval_needed', label: 'Approval needed' },
  { key: 'no_rollover', label: 'No rollover' },
  { key: 'missing_owner', label: 'Missing owner' },
  { key: 'contract_mismatch', label: 'Contract mismatch' },
];
